import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { rupee } from "@/lib/api";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal } = useCart();

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="w-14 h-14 mx-auto text-primary/40" />
        <h1 className="font-display text-4xl font-light mt-6">Your bag is empty</h1>
        <p className="text-muted-foreground mt-2 font-light">Add some comfort to your day.</p>
        <Link to="/shop" className="inline-block mt-7 bg-foreground text-[hsl(var(--ivory))] px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-primary transition-colors" data-testid="empty-cart-shop-btn">Start shopping</Link>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-8">Shopping Bag</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-4">
          {items.map((it, i) => (
            <div key={i} className="flex gap-4 bg-white rounded-2xl border border-border p-4" data-testid={`cart-item-${i}`}>
              <img src={it.image} alt={it.name} className="w-24 h-28 rounded-xl object-cover bg-secondary" />
              <div className="flex-1">
                <h3 className="font-semibold">{it.name}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">{[it.size, it.color].filter(Boolean).join(" · ")}</p>
                <p className="font-mono font-semibold mt-1">{rupee(it.price)}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-border rounded-full">
                    <button onClick={() => updateQty(i, it.qty - 1)} className="p-2" data-testid={`cart-minus-${i}`}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-7 text-center text-sm font-mono">{it.qty}</span>
                    <button onClick={() => updateQty(i, it.qty + 1)} className="p-2" data-testid={`cart-plus-${i}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => removeItem(i)} className="text-muted-foreground hover:text-destructive" data-testid={`cart-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:sticky lg:top-24 h-fit bg-white rounded-2xl border border-border p-6">
          <h2 className="font-semibold text-lg mb-4">Order Summary</h2>
          <Row label="Subtotal" value={rupee(subtotal)} />
          <Row label="Shipping" value="Calculated at confirmation" small />
          <div className="border-t border-border my-4" />
          <Row label="Total" value={rupee(subtotal)} bold />
          <Link to="/checkout" className="block text-center mt-6 bg-foreground text-[hsl(var(--ivory))] py-4 text-xs uppercase tracking-[0.2em] hover:bg-primary transition-colors" data-testid="checkout-btn">
            Proceed to Checkout
          </Link>
          <Link to="/shop" className="block text-center mt-4 text-xs uppercase tracking-[0.15em] text-primary bc-underline mx-auto w-fit">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, bold, small }) => (
  <div className="flex justify-between items-center py-1">
    <span className={`${bold ? "font-semibold" : "text-muted-foreground"} ${small ? "text-xs" : "text-sm"}`}>{label}</span>
    <span className={`${bold ? "font-mono font-bold text-lg" : small ? "text-xs text-muted-foreground" : "font-mono"}`}>{value}</span>
  </div>
);
